// Decides which component renders an attribute, from `html_tag` plus — for headers
// and output payloads, which the backend never modelled as tags — the attribute
// name. Each screen supplies its own ordered rule list; see below for why.

export const KINDS = {
  TEXT: 'text',
  PASSWORD: 'password',
  PASSWORD_HIDDEN: 'passwordHidden',
  CHECKBOX: 'checkbox',
  EMAIL: 'email',
  TEXTAREA: 'textarea',
  JSON_PAYLOAD: 'jsonPayload',
  HEADERS: 'headers',
  LOCATION_SETTINGS: 'locationSettings',
};

export const isHeadersField = (name) => String(name).includes('headers');

export const isOutputPayloadField = (name) =>
  String(name).includes('output_payload');

const isObjectish = ({ html_tag: tag }) =>
  tag === 'object_input' || tag === 'text_area';

// The two screens apply the name check at different points, and the difference is
// observable:
//
//   Global settings   — the name check only happens inside object_input/text_area,
//                       and `email_input` is not handled at all.
//   Location settings — the name check happens after every html_tag rule, so it
//                       catches any unmatched tag, and `location_settings_input`
//                       is not handled at all.
//
// Rather than unify them and silently change behaviour, each keeps its own list.
// Both collapse into one the day the backend sends a dedicated `html_tag`.
export const globalSettingsRules = [
  { match: (d) => d.html_tag === 'text_input', kind: KINDS.TEXT },
  {
    match: (d) => d.html_tag === 'password_input' && d.html_tag_hidden,
    kind: KINDS.PASSWORD_HIDDEN,
  },
  { match: (d) => d.html_tag === 'password_input', kind: KINDS.PASSWORD },
  { match: (d) => d.html_tag === 'checkbox_input', kind: KINDS.CHECKBOX },
  {
    match: (d) => isObjectish(d) && isOutputPayloadField(d.name),
    kind: KINDS.JSON_PAYLOAD,
  },
  {
    match: (d) => isObjectish(d) && isHeadersField(d.name),
    kind: KINDS.HEADERS,
  },
  { match: isObjectish, kind: KINDS.TEXTAREA },
  {
    match: (d) => d.html_tag === 'location_settings_input',
    kind: KINDS.LOCATION_SETTINGS,
  },
];

export const locationSettingsRules = [
  { match: (d) => d.html_tag === 'text_input', kind: KINDS.TEXT },
  {
    match: (d) => d.html_tag === 'password_input' && !d.html_tag_hidden,
    kind: KINDS.PASSWORD,
  },
  {
    match: (d) => d.html_tag === 'password_input' && d.html_tag_hidden,
    kind: KINDS.PASSWORD_HIDDEN,
  },
  { match: (d) => d.html_tag === 'email_input', kind: KINDS.EMAIL },
  { match: (d) => d.html_tag === 'checkbox_input', kind: KINDS.CHECKBOX },
  {
    match: (d) =>
      isObjectish(d) &&
      !isOutputPayloadField(d.name) &&
      !isHeadersField(d.name),
    kind: KINDS.TEXTAREA,
  },
  { match: (d) => isOutputPayloadField(d.name), kind: KINDS.JSON_PAYLOAD },
  { match: (d) => isHeadersField(d.name), kind: KINDS.HEADERS },
];

export const createFieldResolver =
  (rules) =>
  (descriptor = {}) =>
    rules.find(({ match }) => match(descriptor))?.kind ?? null;

export const resolveGlobalSettingsKind =
  createFieldResolver(globalSettingsRules);

export const resolveLocationSettingsKind = createFieldResolver(
  locationSettingsRules,
);
